import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Heart, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * SaveThisChip — iter201
 *
 * A soft-tone follow-the-scroll signup CTA on campsite detail pages,
 * pinned bottom-right on desktop and bottom-centre-right on mobile
 * (thumb-reach). Non-intrusive: it's a chip, not a modal — always
 * visible, one-tap dismiss with a fresh reappearance on the next
 * campsite. Only renders for anonymous visitors.
 *
 * Design goals:
 *   • Never overlaps the chat/help widget (bottom-right pinned)
 *   • Never covers the affiliate booking CTAs (offset a little higher)
 *   • Dismiss is per-campsite (localStorage key includes pathname) so
 *     a returning visitor sees the chip again on every new listing.
 */
export default function SaveThisChip({ campsiteName }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const dismissKey = `ccu:save-chip:${location.pathname}`;

  useEffect(() => {
    setDismissed(false);
    try {
      if (localStorage.getItem(dismissKey)) setDismissed(true);
    } catch { /* private mode */ }
  }, [dismissKey]);

  if (loading || user || dismissed) return null;

  const close = () => {
    setDismissed(true);
    try { localStorage.setItem(dismissKey, "1"); } catch { /* ignore */ }
  };

  const nextUrl = encodeURIComponent(location.pathname + location.search);
  const nameShort = (campsiteName || "this campsite").length > 24
    ? (campsiteName || "this campsite").slice(0, 22) + "…"
    : (campsiteName || "this campsite");

  return (
    <div
      className="fixed z-[60] bottom-24 right-3 sm:bottom-6 sm:right-6 max-w-[calc(100vw-2rem)] animate-in fade-in slide-in-from-bottom-2"
      data-testid="save-this-chip"
      role="complementary"
      aria-label={`Sign up free to save ${nameShort}`}
    >
      <div className="flex items-stretch bg-[#0e0e0e] text-white shadow-[0_6px_0_0_rgba(0,0,0,0.35)] border-2 border-[#ff4500]">
        <Link
          to={`/register?next=${nextUrl}`}
          className="flex items-center gap-2 pl-3 pr-2 py-2 hover:bg-[#1a1a1a] transition-colors"
          data-testid="save-this-chip-cta"
        >
          <span className="w-6 h-6 shrink-0 bg-[#ff4500] flex items-center justify-center rounded-full">
            <Heart className="w-3 h-3" strokeWidth={2.5} fill="white" />
          </span>
          <span className="text-[11px] mono uppercase tracking-widest leading-tight">
            <span className="block font-black">Sign up free</span>
            <span className="block text-[9px] text-white/70 normal-case tracking-wide">Save {nameShort}</span>
          </span>
        </Link>
        <button
          type="button"
          onClick={close}
          aria-label="Dismiss save chip"
          className="pl-2 pr-2 py-1 text-white/50 hover:text-white transition-colors border-l border-white/15"
          data-testid="save-this-chip-close"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
